import { FadeUp, SectionHeading, CTASection } from "../components/Shared";
import { IMAGES } from "../lib/brand";

const PROCESSES = [
  {
    title: "Sheet Metal Fabrication",
    img: IMAGES.sheetMetal,
    desc: "Precision laser cutting, CNC bending, punching and forming for enclosures, brackets, chassis and structural components.",
    specs: ["Materials: Steel, SS, Aluminum, Copper, Brass", "Thickness: 0.5mm – 20mm", "Tolerance: ±0.1mm", "Finishes: Powder coat, plating, anodizing"],
  },
  {
    title: "CNC Machining",
    img: IMAGES.cnc,
    desc: "3, 4 and 5-axis milling and turning for high-precision components across prototyping and production volumes.",
    specs: ["Materials: Metals & engineering plastics", "Tolerance: down to ±0.01mm", "Volumes: 1 – 100,000+ parts", "In-process CMM inspection"],
  },
  {
    title: "Fabrication & Welding",
    img: IMAGES.roboticsFactory,
    desc: "Certified MIG, TIG and spot welding with structural fabrication, frames, skids and welded assemblies.",
    specs: ["Certified welders & WPS documentation", "Structural steel & stainless", "Sub-assembly & full assembly", "NDT available on request"],
  },
  {
    title: "Injection Molding",
    img: IMAGES.injectionMolding,
    desc: "Rapid tooling and production molding for thermoplastic parts — from bridge tooling to multi-cavity production molds.",
    specs: ["Prototype & production tooling", "50 – 1,000,000+ shots", "Overmolding & insert molding", "Wide resin library"],
  },
  {
    title: "3D Printing",
    img: IMAGES.printing3d,
    desc: "Additive manufacturing across FDM, SLA, SLS and metal printing for rapid prototypes and complex end-use parts.",
    specs: ["Lead times from 2 days", "Engineering resins & metals", "Layer resolution to 25µm", "Design-for-additive support"],
  },
  {
    title: "Surface Finishing",
    img: IMAGES.surfaceFinishing,
    desc: "Complete post-processing: anodizing, powder coating, electroplating, passivation, bead blasting and polishing.",
    specs: ["Cosmetic & functional finishes", "Color matching to RAL/Pantone", "Corrosion protection systems", "Certificates of conformance"],
  },
];

export default function Manufacturing() {
  return (
    <div data-testid="manufacturing-page">
      <section className="py-28 sm:py-36 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <FadeUp>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-primary mb-6 font-mono-tech">Manufacturing Capabilities</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter max-w-4xl leading-[1.05]">
              Precision processes for <span className="font-medium text-primary">every stage of production.</span>
            </h1>
            <p className="mt-8 text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed">
              One purchase order, six core processes, hundreds of material and finish combinations — all quality-managed by Vectron.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 space-y-24">
          {PROCESSES.map((p, i) => (
            <FadeUp key={p.title}>
              <div className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center ${i % 2 ? "lg:[&>*:first-child]:order-2" : ""}`} data-testid={`process-section-${i}`}>
                <div className="relative rounded-xl overflow-hidden border border-border group">
                  <img src={p.img} alt={p.title} className="w-full h-80 sm:h-96 object-cover transition-transform duration-500 group-hover:scale-105" />
                  <span className="absolute top-4 left-4 font-mono-tech text-[11px] font-bold tracking-[0.25em] text-white bg-slate-950/70 backdrop-blur px-3 py-1.5 rounded-full">
                    PROCESS / 0{i + 1}
                  </span>
                </div>
                <div>
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight font-heading">{p.title}</h2>
                  <p className="mt-5 text-muted-foreground leading-relaxed">{p.desc}</p>
                  <ul className="mt-8 grid sm:grid-cols-2 gap-3">
                    {p.specs.map((s) => (
                      <li key={s} className="text-sm px-4 py-3 rounded-lg bg-muted border border-border text-foreground/90">{s}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      <CTASection
        title="Have drawings ready?"
        description="Send us your CAD files and specifications — our engineering team will respond with a detailed quote and DFM feedback."
      />
    </div>
  );
}
